import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatTalkTime, isMolaBreak } from "@/utils/helpers";

const DAY_PALETTE = [
  { stroke: "#3b82f6", fill: "rgba(59,130,246,0.18)", soft: "bg-blue-500/15 border-blue-500/40 text-blue-500" },
  { stroke: "#10b981", fill: "rgba(16,185,129,0.18)", soft: "bg-emerald-500/15 border-emerald-500/40 text-emerald-500" },
  { stroke: "#f59e0b", fill: "rgba(245,158,11,0.2)", soft: "bg-amber-500/15 border-amber-500/40 text-amber-500" },
  { stroke: "#8b5cf6", fill: "rgba(139,92,246,0.18)", soft: "bg-violet-500/15 border-violet-500/40 text-violet-500" },
  { stroke: "#f43f5e", fill: "rgba(244,63,94,0.18)", soft: "bg-rose-500/15 border-rose-500/40 text-rose-500" },
  { stroke: "#06b6d4", fill: "rgba(6,182,212,0.18)", soft: "bg-cyan-500/15 border-cyan-500/40 text-cyan-500" },
  { stroke: "#f97316", fill: "rgba(249,115,22,0.18)", soft: "bg-orange-500/15 border-orange-500/40 text-orange-500" },
  { stroke: "#d946ef", fill: "rgba(217,70,239,0.18)", soft: "bg-fuchsia-500/15 border-fuchsia-500/40 text-fuchsia-500" },
];

const CHART = {
  width: 860,
  height: 260,
  padL: 52,
  padR: 16,
  padT: 18,
  padB: 36,
};

const formatReportDate = (dateStr) => {
  if (!dateStr) return "-";
  const parts = dateStr.split("-");
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}.${parts[1]}.${parts[0]}`;
};

const parseClockToMinutes = (clock) => {
  if (!clock || typeof clock !== "string") return null;
  const [h, m] = clock.split(":").map((v) => parseInt(v, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

const formatAxisTime = (minutes) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const formatYLabel = (minutes) => {
  if (minutes >= 60) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}.${String(m).padStart(2, "0")} saat` : `${h} saat`;
  }
  return `${minutes} dk`;
};

const sortDaysChronological = (days) =>
  [...(days || [])].sort((a, b) => String(a.date).localeCompare(String(b.date)));

const getDayShiftRange = (day) => {
  const start = parseClockToMinutes(day.scheduled_start) ?? 9 * 60 + 30;
  const end = parseClockToMinutes(day.scheduled_end) ?? 18 * 60;
  return { start, end: Math.max(end, start + 60) };
};

const niceYMax = (value) => {
  const v = Math.max(10, value);
  if (v <= 30) return Math.ceil(v / 10) * 10;
  if (v <= 90) return Math.ceil(v / 15) * 15;
  return Math.ceil(v / 30) * 30;
};

const buildHourBuckets = (xMin, xMax) => {
  const buckets = [];
  for (let t = xMin; t < xMax; t += 60) {
    buckets.push({ timeMin: t, minutes: 0, breaks: [] });
  }
  return buckets;
};

const distributeBreakToBuckets = (buckets, breakRow) => {
  const startMin = parseClockToMinutes(breakRow.start_time);
  const endMin = parseClockToMinutes(breakRow.end_time) ?? startMin;
  if (startMin == null) return;

  const totalSec = breakRow.duration_seconds || Math.max(0, (endMin - startMin) * 60);
  if (totalSec <= 0) return;

  buckets.forEach((bucket) => {
    const bucketStart = bucket.timeMin;
    const bucketEnd = bucket.timeMin + 60;
    const overlapStart = Math.max(startMin, bucketStart);
    const overlapEnd = Math.min(endMin, bucketEnd);
    if (overlapEnd > overlapStart) {
      const span = Math.max(1, endMin - startMin);
      const overlapSec = overlapEnd - overlapStart;
      const shareMin = (totalSec * overlapSec) / span / 60;
      bucket.minutes += shareMin;
      bucket.breaks.push(breakRow);
    }
  });
};

function buildChartModel(days) {
  const sorted = sortDaysChronological(days);
  let xMin = Infinity;
  let xMax = -Infinity;
  let yMax = 10;

  sorted.forEach((day) => {
    const { start, end } = getDayShiftRange(day);
    xMin = Math.min(xMin, start);
    xMax = Math.max(xMax, end);
  });

  if (!Number.isFinite(xMin)) {
    xMin = 9 * 60 + 30;
    xMax = 18 * 60;
  }

  xMin = Math.floor(xMin / 60) * 60;
  xMax = Math.ceil(xMax / 60) * 60;
  if (xMax <= xMin) xMax = xMin + 8 * 60;

  const globalBuckets = buildHourBuckets(xMin, xMax);

  const daySeries = sorted.map((day, idx) => {
    const { start, end } = getDayShiftRange(day);
    const buckets = globalBuckets.map((b) => ({ ...b, minutes: 0, breaks: [] }));

    (day.breaks || [])
      .filter(isMolaBreak)
      .forEach((b) => distributeBreakToBuckets(buckets, b));

    buckets.forEach((b) => {
      yMax = Math.max(yMax, Math.ceil(b.minutes));
    });

    return {
      day,
      theme: DAY_PALETTE[idx % DAY_PALETTE.length],
      shiftStart: start,
      shiftEnd: end,
      points: buckets.map((b) => ({
        x: b.timeMin + 30,
        y: Math.round(b.minutes * 10) / 10,
        hourLabel: formatAxisTime(b.timeMin),
        breaks: b.breaks,
        inShift: b.timeMin >= start && b.timeMin < end,
      })),
    };
  });

  const avgPoints = globalBuckets.map((bucket, i) => {
    const sum = daySeries.reduce((acc, s) => acc + (s.points[i]?.y ?? 0), 0);
    return {
      x: bucket.timeMin + 30,
      y: Math.round((sum / Math.max(1, daySeries.length)) * 10) / 10,
      hourLabel: formatAxisTime(bucket.timeMin),
    };
  });

  avgPoints.forEach((p) => {
    yMax = Math.max(yMax, Math.ceil(p.y));
  });

  return {
    daySeries,
    avgPoints,
    xMin,
    xMax,
    yMax: niceYMax(yMax),
  };
}

const scaleX = (minutes, xMin, xMax, innerW) =>
  CHART.padL + ((minutes - xMin) / Math.max(1, xMax - xMin)) * innerW;

const scaleY = (minutes, yMax, innerH) =>
  CHART.padT + innerH - (minutes / Math.max(1, yMax)) * innerH;

function catmullRomPath(points, xMin, xMax, yMax, innerW, innerH) {
  if (points.length === 0) return "";
  if (points.length === 1) {
    const px = scaleX(points[0].x, xMin, xMax, innerW);
    const py = scaleY(points[0].y, yMax, innerH);
    return `M ${px} ${py}`;
  }

  const scaled = points.map((p) => ({
    x: scaleX(p.x, xMin, xMax, innerW),
    y: scaleY(p.y, yMax, innerH),
  }));

  let d = `M ${scaled[0].x} ${scaled[0].y}`;
  for (let i = 0; i < scaled.length - 1; i += 1) {
    const p0 = scaled[i - 1] || scaled[i];
    const p1 = scaled[i];
    const p2 = scaled[i + 1];
    const p3 = scaled[i + 2] || p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}

function areaPath(linePath, points, xMin, xMax, yMax, innerW, innerH) {
  if (!points.length) return "";
  const baseY = scaleY(0, yMax, innerH);
  const firstX = scaleX(points[0].x, xMin, xMax, innerW);
  const lastX = scaleX(points[points.length - 1].x, xMin, xMax, innerW);
  return `${linePath} L ${lastX} ${baseY} L ${firstX} ${baseY} Z`;
}

function BreakShiftTimelineChart({ days, isDark, labelClass, textClass, forceExpanded = false }) {
  const [expanded, setExpanded] = useState(true);
  const isOpen = forceExpanded || expanded;
  const [activeDay, setActiveDay] = useState("all");
  const [hoverPoint, setHoverPoint] = useState(null);

  const model = useMemo(() => buildChartModel(days), [days]);
  const { daySeries, avgPoints, xMin, xMax, yMax } = model;

  const innerW = CHART.width - CHART.padL - CHART.padR;
  const innerH = CHART.height - CHART.padT - CHART.padB;

  const xTicks = useMemo(() => {
    const ticks = [];
    for (let t = xMin; t <= xMax; t += 60) ticks.push(t);
    return ticks;
  }, [xMin, xMax]);

  const yTicks = useMemo(() => {
    const step = yMax <= 30 ? 10 : yMax <= 90 ? 15 : 30;
    const ticks = [];
    for (let t = 0; t <= yMax; t += step) ticks.push(t);
    if (ticks[ticks.length - 1] !== yMax) ticks.push(yMax);
    return ticks;
  }, [yMax]);

  const visibleSeries = useMemo(() => {
    if (activeDay === "all") return daySeries;
    return daySeries.filter((s) => s.day.date === activeDay);
  }, [activeDay, daySeries]);

  const showAverage = activeDay === "all" && daySeries.length > 1;

  if (daySeries.length === 0) return null;

  return (
    <div
      className={`break-shift-timeline rounded-xl border overflow-hidden ${
        isDark ? "border-slate-800 bg-slate-900/30" : "border-gray-200 bg-white"
      }`}
    >
      <button
        type="button"
        onClick={() => !forceExpanded && setExpanded((open) => !open)}
        disabled={forceExpanded}
        className={`w-full flex items-center justify-between gap-3 px-3 sm:px-4 py-2.5 text-left transition-colors ${isDark ? "hover:bg-slate-800/60" : "hover:bg-gray-50"}`}
      >
        <div className="flex items-start gap-2 min-w-0">
          <div className={`mt-0.5 shrink-0 ${isDark ? "text-amber-400" : "text-amber-600"}`}>
            {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
          <div className="min-w-0">
            <p className={`text-xs font-semibold flex items-center gap-1.5 ${textClass || (isDark ? "text-slate-200" : "text-gray-900")}`}>
              <Clock className="w-3.5 h-3.5 text-amber-500 shrink-0" />
              Grafiksel Özet · Mesai Mola Eğrisi
            </p>
            <p className={`text-[10px] mt-0.5 ${labelClass}`}>
              Saat dilimlerine göre mola yoğunluğu · Yükseklik = mola süresi (dk)
            </p>
          </div>
        </div>
        <Badge
          variant="outline"
          className={`shrink-0 text-[10px] h-5 ${isDark ? "border-slate-600 text-slate-400" : "border-gray-300 text-gray-600"}`}
        >
          {daySeries.length} gün
        </Badge>
      </button>

      {isOpen && (
      <div className={`p-3 sm:p-4 overflow-x-auto border-t ${isDark ? "border-slate-800 bg-gradient-to-b from-slate-900/80 to-slate-950/50" : "border-gray-100 bg-gradient-to-b from-white to-amber-50/30"}`}>
        <svg
          viewBox={`0 0 ${CHART.width} ${CHART.height}`}
          className="w-full max-w-full block"
          style={{ minWidth: 520, height: "auto" }}
          role="img"
          aria-label="Mesai mola eğrisi grafiği"
        >
          <defs>
            {daySeries.map((s) => (
              <linearGradient key={`grad-${s.day.date}`} id={`break-grad-${s.day.date}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={s.theme.stroke} stopOpacity={isDark ? 0.35 : 0.28} />
                <stop offset="100%" stopColor={s.theme.stroke} stopOpacity={0.02} />
              </linearGradient>
            ))}
            <linearGradient id="break-grad-avg" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={isDark ? "#94a3b8" : "#64748b"} stopOpacity={0.2} />
              <stop offset="100%" stopColor={isDark ? "#94a3b8" : "#64748b"} stopOpacity={0.02} />
            </linearGradient>
          </defs>

          {yTicks.map((tick) => {
            const y = scaleY(tick, yMax, innerH);
            return (
              <g key={`y-${tick}`}>
                <line
                  x1={CHART.padL}
                  y1={y}
                  x2={CHART.width - CHART.padR}
                  y2={y}
                  stroke={isDark ? "rgba(51,65,85,0.65)" : "rgba(226,232,240,0.95)"}
                  strokeWidth={1}
                  strokeDasharray={tick === 0 ? undefined : "4 4"}
                />
                <text
                  x={CHART.padL - 8}
                  y={y + 4}
                  textAnchor="end"
                  className="fill-current"
                  style={{ fontSize: 10, fill: isDark ? "#94a3b8" : "#64748b" }}
                >
                  {formatYLabel(tick)}
                </text>
              </g>
            );
          })}

          {xTicks.map((tick) => {
            const x = scaleX(tick, xMin, xMax, innerW);
            return (
              <g key={`x-${tick}`}>
                <line
                  x1={x}
                  y1={CHART.padT}
                  x2={x}
                  y2={CHART.height - CHART.padB}
                  stroke={isDark ? "rgba(51,65,85,0.35)" : "rgba(241,245,249,0.95)"}
                  strokeWidth={1}
                />
                <text
                  x={x}
                  y={CHART.height - 12}
                  textAnchor="middle"
                  style={{ fontSize: 10, fill: isDark ? "#94a3b8" : "#64748b" }}
                >
                  {formatAxisTime(tick)}
                </text>
              </g>
            );
          })}

          <rect
            x={scaleX(xMin, xMin, xMax, innerW)}
            y={CHART.padT}
            width={scaleX(xMax, xMin, xMax, innerW) - scaleX(xMin, xMin, xMax, innerW)}
            height={innerH}
            fill={isDark ? "rgba(15,23,42,0.35)" : "rgba(248,250,252,0.85)"}
            rx={8}
          />

          {showAverage && (() => {
            const line = catmullRomPath(avgPoints, xMin, xMax, yMax, innerW, innerH);
            const area = areaPath(line, avgPoints, xMin, xMax, yMax, innerW, innerH);
            return (
              <g opacity={0.85}>
                <path d={area} fill="url(#break-grad-avg)" />
                <path
                  d={line}
                  fill="none"
                  stroke={isDark ? "#94a3b8" : "#64748b"}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  strokeLinecap="round"
                />
              </g>
            );
          })()}

          {visibleSeries.map((series) => {
            const line = catmullRomPath(series.points, xMin, xMax, yMax, innerW, innerH);
            const area = areaPath(line, series.points, xMin, xMax, yMax, innerW, innerH);
            const isSingle = activeDay !== "all" || daySeries.length === 1;

            return (
              <g key={series.day.date} opacity={activeDay === "all" && daySeries.length > 1 ? 0.88 : 1}>
                {(isSingle || activeDay === series.day.date) && (
                  <path d={area} fill={`url(#break-grad-${series.day.date})`} />
                )}
                <path
                  d={line}
                  fill="none"
                  stroke={series.theme.stroke}
                  strokeWidth={activeDay === "all" && daySeries.length > 1 ? 2 : 2.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {series.points.map((pt, i) => {
                  if (pt.y <= 0) return null;
                  const px = scaleX(pt.x, xMin, xMax, innerW);
                  const py = scaleY(pt.y, yMax, innerH);
                  const tipId = `${series.day.date}::${i}`;
                  return (
                    <circle
                      key={tipId}
                      cx={px}
                      cy={py}
                      r={hoverPoint === tipId ? 5 : 3.5}
                      fill={series.theme.stroke}
                      stroke={isDark ? "#0f172a" : "#fff"}
                      strokeWidth={2}
                      className="cursor-pointer"
                      onMouseEnter={() => setHoverPoint(tipId)}
                      onMouseLeave={() => setHoverPoint(null)}
                    />
                  );
                })}
              </g>
            );
          })}

          <line
            x1={CHART.padL}
            y1={CHART.height - CHART.padB}
            x2={CHART.width - CHART.padR}
            y2={CHART.height - CHART.padB}
            stroke={isDark ? "rgba(148,163,184,0.5)" : "rgba(148,163,184,0.8)"}
            strokeWidth={1}
          />
          <line
            x1={CHART.padL}
            y1={CHART.padT}
            x2={CHART.padL}
            y2={CHART.height - CHART.padB}
            stroke={isDark ? "rgba(148,163,184,0.5)" : "rgba(148,163,184,0.8)"}
            strokeWidth={1}
          />
        </svg>

        <div className="mt-3 flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => setActiveDay("all")}
            className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors ${
              activeDay === "all"
                ? isDark
                  ? "border-amber-500/50 bg-amber-500/15 text-amber-300"
                  : "border-amber-300 bg-amber-50 text-amber-800"
                : isDark
                  ? "border-slate-700 text-slate-400 hover:border-slate-600"
                  : "border-gray-200 text-gray-600 hover:border-gray-300"
            }`}
          >
            Tüm günler
          </button>
          {daySeries.map((series) => (
            <button
              key={series.day.date}
              type="button"
              onClick={() => setActiveDay(series.day.date)}
              className={`text-[10px] px-2.5 py-1 rounded-full border transition-colors inline-flex items-center gap-1.5 ${
                activeDay === series.day.date
                  ? `${series.theme.soft} border-current`
                  : isDark
                    ? "border-slate-700 text-slate-400 hover:border-slate-600"
                    : "border-gray-200 text-gray-600 hover:border-gray-300"
              }`}
            >
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: series.theme.stroke }} />
              {formatReportDate(series.day.date)}
            </button>
          ))}
          {showAverage && (
            <span className={`text-[10px] px-2 py-1 inline-flex items-center gap-1.5 ${labelClass}`}>
              <span className="w-4 h-0 border-t-2 border-dashed border-slate-400" />
              Günlük ortalama
            </span>
          )}
        </div>

        {hoverPoint && (() => {
          const sep = hoverPoint.lastIndexOf("::");
          if (sep < 0) return null;
          const date = hoverPoint.slice(0, sep);
          const idx = Number(hoverPoint.slice(sep + 2));
          const series = daySeries.find((s) => s.day.date === date);
          const pt = series?.points[idx];
          if (!series || !pt) return null;
          return (
            <div
              className={`mt-2 text-[11px] rounded-lg border px-3 py-2 inline-block shadow-sm ${
                isDark ? "border-amber-500/30 bg-slate-800/95 text-slate-100" : "border-amber-200 bg-white text-gray-800"
              }`}
            >
              <span className="font-semibold">{formatReportDate(series.day.date)}</span>
              {" · "}
              <span className="font-mono">{pt.hourLabel}</span>
              {" · "}
              <span className="font-semibold text-amber-500">{Math.round(pt.y)} dk mola</span>
              {pt.breaks?.length > 0 && (
                <span className={labelClass}>
                  {" · "}
                  {pt.breaks.length} mola kaydı
                </span>
              )}
            </div>
          );
        })()}

        <div className={`mt-2 text-[10px] ${labelClass}`}>
          {daySeries.map((s) => (
            <span key={`meta-${s.day.date}`} className="mr-3 inline-block">
              <span className="font-mono font-medium">{formatReportDate(s.day.date)}</span>
              {s.day.scheduled_start && s.day.scheduled_end && (
                <span> · mesai {s.day.scheduled_start}–{s.day.scheduled_end}</span>
              )}
              <span> · {formatTalkTime(s.day.between_break_seconds || 0)} toplam mola</span>
            </span>
          ))}
        </div>
      </div>
      )}
    </div>
  );
}

export default BreakShiftTimelineChart;
